import { Link, useLocation } from "react-router-dom";
import { ArrowRight, Menu } from "lucide-react";
import { useState } from "react";
import { Logo } from "@/components/brand/Logo";
import { useAuth } from "@/context/AuthContext";

export const PUBLIC_NAV = [
  { label: "Products", to: "/products" },
  { label: "Solutions", to: "/solutions" },
  { label: "Marketplace", to: "/marketplace" },
  { label: "Learning", to: "/learning" },
  { label: "Opportunities", to: "/opportunities" },
  { label: "Pricing", to: "/pricing" },
];

export function PublicNav() {
  const { user } = useAuth();
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const primaryTo = user ? "/adaptive" : "/register";
  const isActive = (to) => pathname === to || (to !== "/" && pathname.startsWith(to));

  return (
    <header className="sticky top-0 z-50 border-b border-slate-200/70 bg-[#f7f4ec]/85 backdrop-blur-md" data-testid="public-nav">
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-5 lg:px-8">
        <Link to="/" data-testid="public-nav-logo"><Logo variant="dark" height={26} /></Link>
        <nav className="hidden items-center gap-7 text-[13px] font-medium text-slate-600 lg:flex">
          {PUBLIC_NAV.map((n) => (
            <Link
              key={n.label}
              to={n.to}
              data-testid={`public-nav-${n.label.toLowerCase()}`}
              className={`transition-colors ${
                isActive(n.to)
                  ? "rounded-full bg-slate-900/5 px-3 py-1.5 font-semibold text-slate-900"
                  : "hover:text-slate-900"
              }`}
            >
              {n.label}
            </Link>
          ))}
        </nav>
        <div className="flex items-center gap-2.5">
          <Link to="/login" data-testid="public-signin" className="hidden rounded-xl border border-slate-300 px-4 py-2 text-[13px] font-semibold text-slate-800 transition-colors hover:border-slate-400 sm:block">Sign In</Link>
          <Link to={primaryTo} data-testid="public-getstarted" className="flex items-center gap-1.5 rounded-xl bg-[#b0862f] px-4 py-2 text-[13px] font-semibold text-white transition-colors hover:bg-[#9a742a]">Get Started <ArrowRight size={15} /></Link>
          <button onClick={() => setOpen((v) => !v)} className="lg:hidden" aria-label="Menu" data-testid="public-nav-mobile-toggle"><Menu size={20} className="text-slate-700" /></button>
        </div>
      </div>
      {open && (
        <nav className="border-t border-slate-200 bg-[#f7f4ec] px-5 py-3 lg:hidden" data-testid="public-nav-mobile">
          <div className="flex flex-col gap-1">
            {PUBLIC_NAV.map((n) => (
              <Link key={n.label} to={n.to} onClick={() => setOpen(false)} className={`rounded-lg px-3 py-2 text-[14px] font-medium ${isActive(n.to) ? "bg-slate-900/5 text-slate-900" : "text-slate-600"}`}>{n.label}</Link>
            ))}
          </div>
        </nav>
      )}
    </header>
  );
}

export function PublicFooter() {
  return (
    <footer className="relative overflow-hidden bg-[#060A14] text-slate-300" data-testid="public-footer">
      <div className="hero-aurora pointer-events-none absolute inset-0 opacity-40" />
      <div className="grain-fine pointer-events-none absolute inset-0 opacity-30" />
      <div className="relative mx-auto flex max-w-7xl flex-col gap-6 px-5 py-12 lg:flex-row lg:items-center lg:justify-between lg:px-8">
        <div>
          <Link to="/"><Logo variant="light" height={24} /></Link>
          <p className="mt-3 font-mono text-[11px] uppercase tracking-[0.18em] text-slate-500">People. Plan. Opportunity.</p>
        </div>
        <div className="flex flex-wrap gap-x-6 gap-y-2 text-[13px] text-slate-400">
          {PUBLIC_NAV.map((n) => (
            <Link key={n.label} to={n.to} className="transition-colors hover:text-white">{n.label}</Link>
          ))}
        </div>
      </div>
      <div className="relative border-t border-white/10 py-4 text-center font-mono text-[10px] uppercase tracking-[0.18em] text-slate-500">
        © 2026 Millionaire Mindsetters Technologies · All rights reserved.
      </div>
    </footer>
  );
}

export function PublicHero({ eyebrow, title, accent, sub, children, visual }) {
  return (
    <div className="relative overflow-hidden" data-testid="public-hero">
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(60%_60%_at_12%_-10%,rgba(26,95,208,0.09),transparent_60%),radial-gradient(45%_55%_at_98%_-5%,rgba(176,134,47,0.11),transparent_60%)]" />
      <div className="relative mx-auto max-w-7xl px-5 pt-16 lg:px-8">
        <div className={visual ? "grid items-center gap-8 lg:grid-cols-[1.35fr_1fr]" : ""}>
          <div>
            <div className="inline-flex items-center gap-2 rounded-full border border-[#c9a24b]/40 bg-white/60 px-3 py-1 font-mono text-[10px] font-bold uppercase tracking-[0.22em] text-[#a37d2a] backdrop-blur">
              <span className="h-1.5 w-1.5 rounded-full bg-[#c9a24b]" />{eyebrow}
            </div>
            <h1 className="mt-4 max-w-4xl font-serif-display text-[42px] font-semibold leading-[1.03] tracking-tight text-slate-900 sm:text-5xl lg:text-6xl">
              {title} {accent && <span className="text-foil">{accent}</span>}
            </h1>
            {sub && <p className="mt-5 max-w-2xl text-[16px] leading-relaxed text-slate-500">{sub}</p>}
            {children}
          </div>
          {visual}
        </div>
        <div className="divider-glow mt-12 w-full" />
      </div>
    </div>
  );
}
